import React, { useEffect, useState } from "react";
import {
  BarChart3,
  Building2,
  Gauge,
  Clock,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { stats as fetchStats } from "@/lib/adminApi";

const BADGE_COLORS = {
  "Empresa Emergente": "#DC2626",
  "Empresa em Evolução": "#FACC15",
  "Empresa Estruturada": "#65A30D",
  "Empresa de Alta Performance": "#16A34A",
};

const MONTH_LABEL_PT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

function labelMonth(key) {
  const [y, m] = key.split("-");
  return `${MONTH_LABEL_PT[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetchStats().then(setData).catch((e) => setErr(e?.message || "Erro"));
  }, []);

  if (err) return <div className="text-red-600 text-sm">{err}</div>;
  if (!data) return <div className="text-slate-500 text-sm">Carregando…</div>;

  const kpis = [
    {
      icon: <BarChart3 size={16} />,
      label: "Diagnósticos realizados",
      value: data.total_diagnostics,
      testid: "kpi-total",
    },
    {
      icon: <Building2 size={16} />,
      label: "Empresas cadastradas",
      value: data.unique_companies,
      testid: "kpi-companies",
    },
    {
      icon: <Gauge size={16} />,
      label: "Índice HUB médio",
      value: `${Math.round(data.avg_hub_index)}`,
      suffix: "/100",
      testid: "kpi-avg",
    },
    {
      icon: <Sparkles size={16} />,
      label: "Novos (30 dias)",
      value: data.last_30_days,
      testid: "kpi-last30",
    },
  ];

  const monthly = data.by_month.map((m) => ({
    month: labelMonth(m.month),
    count: m.count,
  }));

  const badgeData = data.by_badge.map((b) => ({
    ...b,
    fill: BADGE_COLORS[b.name] || "#94A3B8",
  }));

  return (
    <div data-testid="admin-dashboard">
      <div className="hub-eyebrow">Painel HUB</div>
      <h1 className="mt-2 font-display font-medium text-3xl sm:text-4xl tracking-tight">
        Visão executiva
      </h1>
      <p className="mt-2 text-slate-600 max-w-2xl">
        Indicadores agregados de todos os diagnósticos realizados no
        Diagnóstico de Desenvolvimento Empresarial HUB.
      </p>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="hub-card p-5" data-testid={k.testid}>
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-[0.16em] font-semibold">
              {k.icon} {k.label}
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <div className="font-display font-medium text-4xl tracking-tight">
                {k.value}
              </div>
              {k.suffix && (
                <div className="text-slate-400 text-lg">{k.suffix}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Last diagnostic */}
      {data.last_diagnostic && (
        <div className="mt-6 hub-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
              <Clock size={14} /> Último diagnóstico
            </div>
            <div className="mt-1 font-display font-medium text-lg">
              {data.last_diagnostic.company.empresa}
            </div>
            <div className="text-sm text-slate-500">
              {data.last_diagnostic.company.nome} —{" "}
              {new Date(data.last_diagnostic.created_at).toLocaleString("pt-BR")}
            </div>
          </div>
          <Link
            to={`/admin/diagnosticos/${data.last_diagnostic.id}`}
            className="hub-btn-secondary"
            data-testid="last-diagnostic-link"
          >
            Abrir <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Charts */}
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="hub-card p-6">
          <div className="hub-eyebrow">Diagnósticos por mês</div>
          <h3 className="mt-2 font-display font-medium text-lg">
            Volume nos últimos 12 meses
          </h3>
          <div className="mt-6 h-72" data-testid="chart-monthly">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="count" fill="#0F172A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hub-card p-6">
          <div className="hub-eyebrow">Classificação</div>
          <h3 className="mt-2 font-display font-medium text-lg">
            Distribuição por selo
          </h3>
          <div className="mt-6 h-72" data-testid="chart-badges">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={badgeData}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  label={(e) => e.count}
                >
                  {badgeData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hub-card p-6 lg:col-span-2">
          <div className="hub-eyebrow">Média dos 7 pilares</div>
          <h3 className="mt-2 font-display font-medium text-lg">
            Perfil médio da base
          </h3>
          <div className="mt-6 h-72" data-testid="chart-pillars">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.pillar_avg} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" width={170} tick={{ fill: "#0F172A", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${Math.round(v)}%`, "Média"]} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="avg" fill="#D97706" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
